'use client';
import { AppShell, Stack, Burger } from '@mantine/core';
import { useDisclosure } from '@mantine/hooks';
import type { UserRole } from '@shady-pines/db';
import {
  IconMicrophone,
  IconNotification,
  IconBell,
  IconUser,
  IconUsers,
  IconCalendarEvent,
  IconBug,
  IconMusic,
  IconChartBar,
} from '@tabler/icons-react';
import { isNil } from 'lodash';
import { usePathname } from 'next/navigation';
import type { ComponentType } from 'react';
import NavButton from './NavButton';

// Define the common type for all nav items
type NavItem =
  | {
      icon: ComponentType<{ size?: string | number }>;
      label: string;
      path: string;
      admin?: boolean;
    }
  | {
      icon: ComponentType<{ size?: string | number }>;
      label: string;
      children: Array<{ label: string; path: string; admin?: boolean }>;
      admin?: boolean;
    };

export const NAV_ITEMS: readonly NavItem[] = [
  { icon: IconNotification, label: 'Popups', path: '/popups', admin: true },
  { icon: IconBell, label: 'Notifications', path: '/notifications', admin: true },
  { icon: IconUsers, label: 'Users', path: '/users', admin: true },
  { icon: IconBug, label: 'Bug Reports', path: '/bug-reports', admin: true },
  { icon: IconChartBar, label: 'Analytics', path: '/analytics', admin: true },
  {
    icon: IconMicrophone,
    label: 'Shows',
    children: [
      { label: 'Mine', path: '/shows/me' },
      {
        label: 'Schedule',
        path: '/shows/schedule',
      },
      {
        label: 'All',
        path: '/shows/all',
        admin: true,
      },
    ],
  },
  {
    icon: IconCalendarEvent,
    label: 'Events',
    children: [
      { label: 'Events', path: '/events' },
      { label: 'Venues', path: '/venues' },
    ],
  },
  { icon: IconMusic, label: 'Track History', path: '/track-history' },
  { icon: IconUser, label: 'Account', path: '/account' },
] as const;

export function Sidebar({ children, role }: { children: React.ReactNode; role?: UserRole | null }) {
  const pathname = usePathname();
  const [opened, { toggle }] = useDisclosure();

  if (pathname === '/login' || pathname === '/' || pathname.startsWith('/public') || isNil(role)) {
    return children;
  }

  const filterNavItemsForNonAdmin = (items: readonly NavItem[]): NavItem[] => {
    return items
      .filter((item) => !('admin' in item && item.admin))
      .map((item) => {
        if ('children' in item) {
          const filteredChildren = item.children.filter((child) => !child.admin);
          return { ...item, children: filteredChildren } as NavItem;
        }
        return item;
      })
      .filter((item) => {
        if ('children' in item) {
          return item.children.length > 0;
        }
        return true;
      });
  };

  const navItems = role === 'admin' ? NAV_ITEMS : filterNavItemsForNonAdmin(NAV_ITEMS);

  return (
    <AppShell
      header={{ height: 60 }}
      navbar={{
        width: 300,
        breakpoint: 'sm',
        collapsed: { mobile: !opened },
      }}
      padding="md"
    >
      <AppShell.Header>
        <Burger opened={opened} onClick={toggle} hiddenFrom="sm" size="sm" m="md" />
      </AppShell.Header>

      <AppShell.Navbar p="md">
        <Stack gap="sm">
          {navItems.map((item) => (
            <NavButton
              key={'path' in item ? item.path : item.label}
              item={item}
              currentPath={pathname}
              onNavigate={toggle}
            />
          ))}
        </Stack>
      </AppShell.Navbar>

      <AppShell.Main>{children}</AppShell.Main>
    </AppShell>
  );
}
